import React, {type ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import {MenuSectionHeading} from "@vesta/aergia/components/menuSectionHeading";
import {toDataAnalyticsHtmlAttributes, type DataAnalyticsProps} from "@vesta/aergia/utilities/dataAnalytics";
import {hasAValidNumberValue, hasAValidStringValue, tw} from "@vesta/aergia/utilities/utilities";
import {Link} from "@tanstack/react-router";

type ButtonItemProps = {
    iconBefore?: (props: {className?: string}) => ReactNode;
    description?: string;
    title?: string;
    isSelected?: boolean;
    isDisabled?: boolean;
    iconAfter?: (props: {className?: string}) => ReactNode;
    paddingInline?: string;
    paddingBlock?: string;
    className?: string;
    onClick?: () => void;
} & DataAnalyticsProps;

type LinkItemProps = {
    href: string;
    iconBefore?: (props: {className?: string}) => ReactNode;
    description?: string;
    title?: string;
    isSelected?: boolean;
    isDisabled?: boolean;
    iconAfter?: (props: {className?: string}) => ReactNode;
    paddingInline?: string;
    paddingBlock?: string;
    className?: string;
    inSeparateWindow?: boolean;
} & DataAnalyticsProps;

export function MenuGroup({
    children,
    maxHeight,
    minWidth,
    maxWidth,
    className,
}: {
    children:
    | React.ReactElement<typeof Section>
    | React.ReactElement<typeof Section>[]
    | React.ReactElement<typeof ButtonItem>
    | React.ReactElement<typeof ButtonItem>[]
    | React.ReactElement<typeof LinkItem>
    | React.ReactElement<typeof LinkItem>[];
    maxHeight?: number;
    minWidth?: number;
    maxWidth?: number;
    className?: string;
}) {
    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        if (child.type !== Section && child.type !== ButtonItem && child.type !== LinkItem) {
            throw new Error("MenuGroup only accepts Section, ButtonItem or LinkItem as children");
        }
    });

    // const heightClassName = hasAValidNumberValue(maxHeight) ? `max-h-[${maxHeight}px]` : tw`max-h-full`;
    // const maxWidthClassName = hasAValidNumberValue(maxWidth) ? `max-w-[${maxWidth}px]` : tw`max-w-[320px]`;
    const mxClassName = tw`mx-auto`;
    const backgroundClassName = tw`bg-ds-surface-overlay`;

    return (
        <div
            className={twJoin(mxClassName, backgroundClassName, "shadow-ds-shadow-overlay", "rounded-ds-border-radius", className)}
            style={{
                minWidth: hasAValidNumberValue(minWidth) ? minWidth : "",
                maxWidth: hasAValidNumberValue(maxWidth) ? maxWidth : 320,
            }}
        >
            <div
                className={twJoin("flex flex-col", "overflow-auto")}
                style={{
                    maxHeight: hasAValidNumberValue(maxHeight) ? maxHeight : "100%",
                }}
            >
                {children}
            </div>
        </div>
    );
}

export function Section({
    title,
    children,
    hasSeparator,
    isScrollable,
}: {
    title?: string;
    children: React.ReactElement | React.ReactElement[];
    // children:
    //     | React.ReactElement<ButtonItemProps, typeof ButtonItem>
    //     | React.ReactElement<ButtonItemProps, typeof ButtonItem>[]
    //     | React.ReactElement<LinkItemProps, typeof LinkItem>
    //     | React.ReactElement<LinkItemProps, typeof LinkItem>[];
    hasSeparator?: boolean;
    isScrollable?: boolean;
}) {
    // React.Children.forEach(children, (child) => {
    //     if (!React.isValidElement(child)) return;
    //     if (child.type !== ButtonItem && child.type !== LinkItem) {
    //         throw new Error("MenuSection only accepts ButtonItem or LinkItem as children");
    //     }
    // });

    const borderClassName = hasSeparator === true ? tw`border-ds-border border-t-ds-border-width border-solid` : tw`border-0`;
    const scrollClassName = isScrollable === true ? "overflow-auto" : "overflow-visible";

    return (
        <div
            className={twJoin(
                borderClassName,
                scrollClassName,
                "relative",
                "before:content-[''] before:block before:h-[6px]",
                "after:content-[''] after:block after:h-[6px]",
            )}
        >
            {hasAValidStringValue(title) && (
                <MenuSectionHeading
                    className="px-ds-space-200 py-0 mt-[14px] mb-ds-space-075"
                    textColor="text-ds-text-subtle"
                >
                    {title}
                </MenuSectionHeading>
            )}
            {children}
        </div>
    );
}

export function ButtonItem({
    iconBefore: IconBeforeComponent,
    title,
    description,
    isSelected,
    isDisabled,
    iconAfter: IconAfterComponent,
    paddingInline,
    paddingBlock,
    onClick,
    dataAnalyticsEvent = null,
    dataAnalyticsProps = null,
}: Omit<ButtonItemProps, keyof DataAnalyticsProps> & Partial<DataAnalyticsProps>) {
    const activeBackgroundClassName =
        isSelected === true ? tw`bg-ds-background-selected hover:bg-ds-background-selected-hovered active:bg-ds-background-selected-pressed` : "";
    const activeTextClassName = isSelected === true ? tw`text-ds-text-selected active:text-ds-text-selected` : "";
    const cursorClassName = isDisabled === true ? tw`cursor-not-allowed` : tw`cursor-pointer`;
    const hoverClassName = isDisabled === true ? tw`hover:bg-transparent` : tw`hover:bg-ds-background-neutral-subtle-hovered`;
    const focusOutlineClassName = tw`focus-visible:outline-[0.0625rem] outline-0 outline-solid outline-ds-border-selected -outline-offset-ds-border-width`;
    const pxClassName = hasAValidStringValue(paddingInline) ? paddingInline : tw`px-ds-space-200`;
    const pyClassName = hasAValidStringValue(paddingBlock) ? paddingBlock : tw`py-ds-space-100`;
    const backgroundClassName = tw`bg-ds-background-neutral-subtle`;
    const mxClassName = tw`mx-ds-space-0`;
    const myClassName = tw`my-ds-space-0`;

    const textTitleClassName = isDisabled === true ? tw`text-ds-text-disabled` : isSelected === true ? tw`text-ds-text-selected` : tw`text-ds-text`;

    const textDescriptionClassName = isDisabled === true ? tw`text-ds-text-disabled` : tw`text-ds-text-subtlest`;

    const opacityIconClassName = isDisabled === true ? tw`opacity-[0.5]` : tw`opacity-[1]`;
    return (
        <button
            type="button"
            className={twJoin(
                activeBackgroundClassName,
                activeTextClassName,
                cursorClassName,
                hoverClassName,
                focusOutlineClassName,
                pxClassName,
                pyClassName,
                backgroundClassName,
                mxClassName,
                myClassName,
                "min-h-[40px] w-full",
                "flex items-center border-0",
                "relative",
            )}
            onClick={onClick}
            {...toDataAnalyticsHtmlAttributes(dataAnalyticsEvent, dataAnalyticsProps)}
        >
            {isSelected === true && <div className="absolute left-0 h-full w-[2px] bg-ds-text-selected block"></div>}
            <div className="flex grow-1 gap-ds-space-150 items-center justify-center w-full">
                {IconBeforeComponent && (
                    <div className={twJoin(opacityIconClassName, "min-h-[24px] min-w-[24px]", "p-ds-space-050", "flex items-center")}>
                        <IconBeforeComponent className="fill-current" />
                    </div>
                )}
                {(hasAValidStringValue(title) || hasAValidStringValue(description)) && (
                    <div className={twJoin("text-left flex flex-col overflow-hidden w-full")}>
                        {hasAValidStringValue(title) && (
                            <p className={twJoin(textTitleClassName, "whitespace-nowrap overflow-ellipsis", "text-ds-font-body")}>{title}</p>
                        )}
                        {hasAValidStringValue(description) && (
                            <p className={twJoin(textDescriptionClassName, "whitespace-nowrap overflow-ellipsis", "text-ds-font-body-UNSAFE_small")}>
                                {description}
                            </p>
                        )}
                    </div>
                )}
                {IconAfterComponent && (
                    <div className={twJoin(opacityIconClassName, "min-h-[24px] min-w-[24px]", "p-ds-space-050", "flex items-center")}>
                        <IconAfterComponent className="fill-current" />
                    </div>
                )}
            </div>
        </button>
    );
}

export function LinkItem({
    href,
    iconBefore: IconBeforeComponent,
    title,
    description,
    isSelected,
    isDisabled,
    iconAfter: IconAfterComponent,
    paddingInline,
    paddingBlock,
    className,
    inSeparateWindow,
    dataAnalyticsEvent = null,
    dataAnalyticsProps = null,
}: Omit<LinkItemProps, keyof DataAnalyticsProps> & Partial<DataAnalyticsProps>) {
    const activeBackgroundClassName =
        isSelected === true ? tw`bg-ds-background-selected hover:bg-ds-background-selected-hovered active:bg-ds-background-selected-pressed` : "";
    const activeTextClassName = isSelected === true ? tw`text-ds-text-selected active:text-ds-text-selected` : "";
    const cursorClassName = isDisabled === true ? tw`cursor-not-allowed` : tw`cursor-pointer`;
    const hoverClassName = isDisabled === true ? tw`hover:bg-transparent` : tw`hover:bg-ds-background-neutral-subtle-hovered`;
    const focusOutlineClassName = tw`focus-visible:outline-[0.0625rem] outline-0 outline-solid outline-ds-border-selected -outline-offset-ds-border-width`;
    const pxClassName = hasAValidStringValue(paddingInline) ? paddingInline : tw`px-ds-space-200`;
    const pyClassName = hasAValidStringValue(paddingBlock) ? paddingBlock : tw`py-ds-space-100`;
    const backgroundClassName = tw`bg-ds-background-neutral-subtle`;

    const textTitleClassName = isDisabled === true ? tw`text-ds-text-disabled` : isSelected === true ? tw`text-ds-text-selected` : tw`text-ds-text`;

    const textDescriptionClassName = isDisabled === true ? tw`text-ds-text-disabled` : tw`text-ds-text-subtlest`;

    const opacityIconClassName = isDisabled === true ? tw`opacity-[0.5]` : tw`opacity-[1]`;

    return (
        <Link
            to={href}
            target={inSeparateWindow === true ? "_blank" : "_self"}
            className={twJoin(
                activeBackgroundClassName,
                activeTextClassName,
                cursorClassName,
                hoverClassName,
                focusOutlineClassName,
                pxClassName,
                pyClassName,
                backgroundClassName,
                className,
                "min-h-[40px] w-full",
                "flex items-center border-0 no-underline",
                "relative",
            )}
            aria-disabled={isDisabled}
            tabIndex={isDisabled === true ? -1 : 0}
            {...toDataAnalyticsHtmlAttributes(dataAnalyticsEvent, dataAnalyticsProps)}
        >
            {isSelected === true && <div className="absolute left-0 h-full w-[2px] bg-ds-text-selected block"></div>}
            <div className="flex grow-1 gap-ds-space-150 items-center w-full">
                {IconBeforeComponent && (
                    <div className={twJoin(opacityIconClassName, "min-h-[24px] min-w-[24px]", "p-ds-space-050", "flex items-center")}>
                        <IconBeforeComponent className="fill-current" />
                    </div>
                )}
                <div className="text-left flex flex-col overflow-hidden w-full">
                    {hasAValidStringValue(title) && (
                        <p className={twJoin(textTitleClassName, "whitespace-nowrap overflow-ellipsis", "text-ds-font-body")}>{title}</p>
                    )}
                    {hasAValidStringValue(description) && (
                        <p className={twJoin(textDescriptionClassName, "whitespace-nowrap overflow-ellipsis", "text-ds-font-body-UNSAFE_small")}>
                            {description}
                        </p>
                    )}
                </div>
                {IconAfterComponent && (
                    <div className={twJoin(opacityIconClassName, "min-h-[24px] min-w-[24px]", "p-ds-space-050", "flex items-center")}>
                        <IconAfterComponent className="fill-current" />
                    </div>
                )}
            </div>
        </Link>
    );
}
